import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PaginatedReconciliations } from '../interfaces/paginated-reconciliation';
import { Config } from '../app.configs';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class ReconciliationService {
  private readonly CACHE_PREFIX_RECONCILIATIONS = 'reconciliations:';
  private readonly CACHE_PREFIX_OPENING_FEES = 'opening-fees:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getTransactions(
    page: number = 1,
    search: string = '',
    statusFilter: string = '',
    paymentMethodFilter: string = '',
    startDate: string = '',
    endDate: string = '',
    forceRefresh: boolean = false,
  ): Observable<PaginatedReconciliations> {
    const cacheKey = `${this.CACHE_PREFIX_RECONCILIATIONS}${page}:${search}:${statusFilter}:${paymentMethodFilter}:${startDate}:${endDate}`;

    return this.cacheService.get<PaginatedReconciliations>(
      cacheKey,
      () => {
        const params = new HttpParams()
          .set('page', page)
          .set('search', search)
          .set('status', statusFilter)
          .set('payment_method', paymentMethodFilter)
          .set('start_date', startDate)
          .set('end_date', endDate);
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedReconciliations>(
          Config.toApiUrl(`dashboard/reconciliations/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateReconciliationsCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX_RECONCILIATIONS);
  }

  reconciliate(email: string, uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      email: email,
      reconciliation_item_uuid: uuid,
    };

    return this.http
      .post(Config.toApiUrl(`dashboard/reconciliations/`), payload, {
        headers,
        withCredentials: true,
      })
      .pipe(tap(() => this.invalidateReconciliationsCache()));
  }

  refund(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/reconciliations/${uuid}/refund/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateReconciliationsCache()));
  }

  getOpeningFees(uuid: string, forceRefresh: boolean = false): Observable<any> {
    const cacheKey = `${this.CACHE_PREFIX_OPENING_FEES}${uuid}`;

    return this.cacheService.get(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http.get<any>(
          Config.toApiUrl(`dashboard/users/${uuid}/opening-fees/`),
          { headers, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateOpeningFeesCache(uuid: string): void {
    this.cacheService.invalidate(`${this.CACHE_PREFIX_OPENING_FEES}${uuid}`);
  }
}
